import { useMemo, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../../../common/Button"
import {
    getViewDownloadLinkForProcoDocs,
    sendCaseToMoonbirdApi,
    uploadDocumentApi,
} from "../../../../../lib/fetchApi"
import { docsFunctions, documentHasFileId, pickLatestCaseDocumentOfType } from "../../../../../lib/docs"
import { caseActions } from "../../../../../store/case"
import { notificationActions } from "../../../../../store/notifications"
import BodySimpleModalV3 from "../../../../common/modal/BodySimpleModalV3"
import HistoryModal from "../../procodocs/practicesupload/status/HistoryModal"
import historyModalClasses from "../../procodocs/practicesupload/status/HistoryModal.module.css"
import classes from "../../procodocs/practicesupload/status/Status.module.css"
import rowClasses from "./PoliceReportCaseDocumentRow.module.css"

const POLICE_REPORT_DOCUMENT_TYPE_NAME = "Police Report"

function hasNonEmptyTrimmed(value) {
    return String(value ?? "").trim() !== ""
}

function getMissingCasePoliceReportFieldLabels(casePoliceReport) {
    const missing = []
    if (!hasNonEmptyTrimmed(casePoliceReport?.report_type)) {
        missing.push("Report Type")
    }
    if (!hasNonEmptyTrimmed(casePoliceReport?.report_number)) {
        missing.push("Report Number")
    }
    if (!hasNonEmptyTrimmed(casePoliceReport?.report_state)) {
        missing.push("Report State")
    }
    return missing
}

/** True if we have a name pair from DL-specific fields or from profile first/last (API often omits DL name fields). */
function hasPatientNamePairForDlWarning(patient) {
    if (!patient) {
        return false
    }
    const fromDlFields =
        hasNonEmptyTrimmed(patient.driver_license_first_name) &&
        hasNonEmptyTrimmed(patient.driver_license_last_name)
    const fromProfile =
        hasNonEmptyTrimmed(patient.first_name) && hasNonEmptyTrimmed(patient.last_name)
    return fromDlFields || fromProfile
}

/** Police report case document in summary header: upload, or verified row (checks + optional history hover + Moonbird send). */
export default function PoliceReportCaseDocumentRow({ uploadPermission }) {
    const dispatch = useDispatch()
    const userToken = useSelector((state) => state.user.token)
    const activeCase = useSelector((state) => state.case.activeCase)
    const inputRef = useRef(null)
    const [uploadLoading, setUploadLoading] = useState(false)
    const [viewLoading, setViewLoading] = useState(false)
    const [moonbirdLoading, setMoonbirdLoading] = useState(false)
    const [moonbirdConfirmOpen, setMoonbirdConfirmOpen] = useState(false)

    const docType = docsFunctions.returnCaseDocumentTypeByName(POLICE_REPORT_DOCUMENT_TYPE_NAME)
    const typeId = docType?.id

    const missingTypeUploadMessage = !docType
        ? "Police Report case document type is not configured in system settings."
        : undefined

    const latestDoc = useMemo(
        () => pickLatestCaseDocumentOfType(activeCase?.documents, typeId),
        [activeCase?.documents, typeId]
    )

    const patient = activeCase?.patient
    const showDlWarning = Boolean(
        latestDoc && hasPatientNamePairForDlWarning(patient) && !hasNonEmptyTrimmed(patient?.driver_license)
    )

    const missingPoliceReportFields = useMemo(
        () => getMissingCasePoliceReportFieldLabels(activeCase?.case_police_report),
        [activeCase?.case_police_report]
    )
    const showFieldWarning = Boolean(latestDoc && missingPoliceReportFields.length > 0)
    const showPoliceReportWarning = showDlWarning || showFieldWarning

    async function viewDocumentHandler() {
        if (!latestDoc) {
            return
        }
        setViewLoading(true)
        const doc = await getViewDownloadLinkForProcoDocs(
            userToken,
            activeCase.case.id,
            "document",
            latestDoc.id,
            dispatch
        )
        if (doc?.view_url) {
            const win = window.open(doc.view_url, "_blank")
            win?.focus()
        }
        setViewLoading(false)
    }

    async function executeSendToMoonbird() {
        if (!activeCase?.case?.id) {
            return
        }
        setMoonbirdLoading(true)
        const res = await sendCaseToMoonbirdApi(userToken, activeCase.case.id, dispatch)
        if (res?.errorMessage) {
            dispatch(notificationActions.setError(res))
        } else {
            dispatch(
                notificationActions.setError({
                    errorMessage: "Sent to Moonbird.",
                    type: "success",
                })
            )
        }
        setMoonbirdLoading(false)
        setMoonbirdConfirmOpen(false)
    }

    function openMoonbirdConfirm() {
        if (!uploadPermission || !activeCase?.case?.id) {
            return
        }
        setMoonbirdConfirmOpen(true)
    }

    const onFileChange = async (e) => {
        if (!e.target.files?.length) {
            return
        }
        if (missingTypeUploadMessage && (typeId == null || typeId === "")) {
            dispatch(
                notificationActions.setError({
                    errorMessage: missingTypeUploadMessage,
                })
            )
            e.target.value = ""
            return
        }

        const file = e.target.files[0]
        const formData = new FormData()
        formData.append("file", file)
        formData.append("notes", "")
        formData.append("document_type_id", typeId)
        formData.append("practices", "all")

        setUploadLoading(true)
        const upload = await uploadDocumentApi(userToken, activeCase.case.id, formData, dispatch)
        if (upload?.errorMessage) {
            dispatch(notificationActions.setError(upload))
        } else {
            dispatch(caseActions.pushItemToDDocuments(upload))
        }
        setUploadLoading(false)
        e.target.value = ""
    }

    if (!latestDoc && !uploadPermission) {
        return null
    }

    if (!latestDoc && uploadPermission) {
        return (
            <>
                <input ref={inputRef} type="file" value="" onChange={onFileChange} style={{ display: "none" }} />
                <Button
                    title="Upload police report file"
                    click={() => inputRef.current?.click()}
                    isLoading={uploadLoading}
                    btnStyle="document_upload"
                >
                    <i className="ti ti-upload"></i>
                </Button>
            </>
        )
    }

    /** Shown only on the red alert icon hover — not on the green checks (those use HistoryModal for view/verify history only). */
    const policeReportWarningTooltipMessage = (
        <>
            {showDlWarning && (
                <p className={historyModalClasses.dl_warning_text}>
                    Driver license number is missing on the patient profile while the patient has a first and last
                    name on file. Add the license number when required for processing.
                </p>
            )}
            {showFieldWarning && (
                <p className={historyModalClasses.dl_warning_text}>
                    Report Type, Report Number, and Report State are required on the police report section. Missing:{" "}
                    {missingPoliceReportFields.join(", ")}.
                </p>
            )}
        </>
    )

    const policeReportWarningTitle = showDlWarning && showFieldWarning
        ? "Police report issues: driver license and required fields missing"
        : showDlWarning
          ? "Driver license number missing on patient profile"
          : `Missing: ${missingPoliceReportFields.join(", ")}`

    /** Only the checks row is inside HistoryModal so hovering the DL error or Moonbird send control does not open the document tooltip. */
    const checksRowForHistoryHover = (
        <div className={`${classes.document_verified} ${classes.document_item}`}>
            <div className={classes.icon}>
                {documentHasFileId(latestDoc) && (
                    <Button
                        title="View document"
                        btnStyle="btn_simple"
                        click={viewDocumentHandler}
                        isLoading={viewLoading}
                        loaderType="dark"
                    >
                        <i className="ti ti-checks"></i>
                    </Button>
                )}
            </div>
        </div>
    )

    const overlayRoot = typeof document !== "undefined" ? document.getElementById("overlay-root") : null

    const moonbirdConfirmModal =
        moonbirdConfirmOpen && overlayRoot
            ? ReactDOM.createPortal(
                  <div
                      className={rowClasses.confirm_overlay}
                      role="presentation"
                      onClick={() => !moonbirdLoading && setMoonbirdConfirmOpen(false)}
                  >
                      <div
                          className={rowClasses.confirm_card}
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="moonbird-confirm-title"
                          onClick={(e) => e.stopPropagation()}
                      >
                          <h3 id="moonbird-confirm-title" className={rowClasses.confirm_title}>
                              Send to Moonbird?
                          </h3>
                          <p className={rowClasses.confirm_text}>
                              This will send the police report document to Moonbird. Only continue if you intend to
                              submit the current file for processing.
                          </p>
                          <div className={rowClasses.confirm_actions}>
                              <Button
                                  click={() => !moonbirdLoading && setMoonbirdConfirmOpen(false)}
                                  btnStyle="btn_white"
                                  isDisabled={moonbirdLoading}
                              >
                                  Cancel
                              </Button>
                              <Button click={executeSendToMoonbird} isLoading={moonbirdLoading}>
                                  Confirm
                              </Button>
                          </div>
                      </div>
                  </div>,
                  overlayRoot
              )
            : null

    return (
        <>
            <div className={`${classes.document_item_wrapper} ${rowClasses.police_report_doc_tools}`}>
                <HistoryModal
                    history={latestDoc.history ?? []}
                    uploadedDocument={latestDoc}
                    hideVerifyAction={true}
                    appendToMessage={null}
                >
                    {checksRowForHistoryHover}
                </HistoryModal>
                {showPoliceReportWarning && (
                    <BodySimpleModalV3 message={policeReportWarningTooltipMessage} widthAuto>
                        <span className={rowClasses.dl_warning_icon} title={policeReportWarningTitle}>
                            <i className="ti ti-alert-circle" aria-hidden />
                        </span>
                    </BodySimpleModalV3>
                )}
                {latestDoc && !viewLoading && (
                    <span className={rowClasses.moonbird_send_btn}>
                        <Button
                            title={
                                uploadPermission
                                    ? "Send police report to Moonbird"
                                    : "Case summary is read-only — enable edit access to send to Moonbird"
                            }
                            btnStyle="btn_simple"
                            click={openMoonbirdConfirm}
                            isDisabled={!uploadPermission}
                            loaderType="dark"
                        >
                            <i className="ti ti-send" aria-hidden />
                        </Button>
                    </span>
                )}
            </div>
            {moonbirdConfirmModal}
        </>
    )
}
